'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { SiteId, SiteSettings } from '@sizzlenz/shared';
import { SITE_SETTINGS_DEFAULTS, SITE_SETTINGS_METADATA_KEY } from '@sizzlenz/shared';
import { useMetadata } from './useMetadata';

/**
 * Detect the current platform from the environment variable.
 * Falls back to 'sizzlenz' if not set.
 */
function getCurrentSiteId(): SiteId {
  const platform = process.env.NEXT_PUBLIC_SIZZLE_PLATFORM ?? process.env.SIZZLE_PLATFORM ?? 'sizzlenz';
  return platform as SiteId;
}
function getEnvironmentValue(value: string | undefined): string | undefined {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
}

/**
 * Build-time public overrides are available during both SSR and hydration.
 * Keep these limited to non-sensitive branding and SEO values.
 */
export function getEnvironmentSiteSettingsOverrides(): Partial<SiteSettings> {
  const overrides: Partial<SiteSettings> = {};
  const siteName = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_SITE_NAME);
  const domain = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_DOMAIN);
  const ogImageUrl = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_OG_IMAGE_URL);
  const seoTitleSuffix = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_SEO_TITLE_SUFFIX);
  const seoDefaultDescription = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_SEO_DESCRIPTION);
  const locale = getEnvironmentValue(process.env.NEXT_PUBLIC_SIZZLE_LOCALE);
  if (siteName) overrides.siteName = siteName;
  if (domain) overrides.domain = domain;
  if (ogImageUrl) overrides.ogImageUrl = ogImageUrl;
  if (seoTitleSuffix) overrides.seoTitleSuffix = seoTitleSuffix;
  if (seoDefaultDescription) overrides.seoDefaultDescription = seoDefaultDescription;
  if (locale) overrides.locale = locale;
  return overrides;
}
const environmentOverrides = getEnvironmentSiteSettingsOverrides();

/**
 * Hook that provides typed site settings with DB overrides merged on top of code defaults.
 *
 * Usage:
 *   const { settings, isLoading } = useSiteSettings();
 *   settings.siteName // "Sizzle NZ" or whatever is in the DB
 *   settings.features.quizzes // true/false
 *
 * The DB stores a partial SiteSettings object. Code defaults fill in anything not set in the DB.
 * This means the app always works even if the DB row doesn't exist yet.
 */
export function useSiteSettings() {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getCurrentSiteId();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const siteId = t0;
  const defaults = SITE_SETTINGS_DEFAULTS[siteId];
  const {
    data: dbSettings,
    isLoading,
    error
  } = useMetadata(SITE_SETTINGS_METADATA_KEY);
  let t1;
  if ($[1] !== dbSettings) {
    t1 = dbSettings ? {
      ...defaults,
      ...dbSettings,
      socialLinks: {
        ...defaults.socialLinks,
        ...dbSettings.socialLinks
      },
      features: {
        ...defaults.features,
        ...dbSettings.features
      },
      announcementBanner: dbSettings.announcementBanner ?? defaults.announcementBanner
    } : defaults;
    $[1] = dbSettings;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const databaseSettings = t1;
  let t2;
  if ($[3] !== databaseSettings) {
    t2 = {
      ...databaseSettings,
      ...environmentOverrides
    };
    $[3] = databaseSettings;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const settings = t2;
  let t3;
  if ($[5] !== error || $[6] !== isLoading || $[7] !== settings) {
    t3 = {
      settings,
      siteId,
      isLoading,
      error
    };
    $[5] = error;
    $[6] = isLoading;
    $[7] = settings;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/**
 * Helper to get a specific setting value.
 * Useful when you only need one field and don't want to destructure.
 */
export function useSiteSetting(key) {
  const {
    settings
  } = useSiteSettings();
  return settings[key];
}